import { useRef, useState } from 'react'
import { colWidthOf, rowHeightOf, useStore } from '../core/store'
import { ref } from '../core/a1'
import { download, isAccepted, parseFile, toCsv, toXlsxBlob } from '../core/io'
import type { CellBorderStyle, CellStyle } from '../core/types'

const SHEET_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])
const MAX_SHEET_IMAGE_BYTES = 3 * 1024 * 1024
const MAX_WORKBOOK_IMAGE_BYTES = 15 * 1024 * 1024

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('이미지를 읽지 못했습니다.'))
    reader.readAsDataURL(file)
  })
}

function imageSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => reject(new Error('지원하지 않거나 손상된 이미지입니다.'))
    image.src = src
  })
}

const NUM_FORMATS: Array<{ label: string; fmt: string }> = [
  { label: '일반', fmt: '' },
  { label: '1,234', fmt: '#,##0' },
  { label: '1,234.00', fmt: '#,##0.00' },
  { label: '₩1,234', fmt: '₩#,##0' },
  { label: '12.3%', fmt: '0.0%' },
  { label: '2026-01-31', fmt: 'yyyy-mm-dd' },
  { label: '1.2E+03', fmt: '0.0E+00' },
]

export default function Toolbar({
  onToast,
  onOpenPdf,
  onOpenTemplates,
}: {
  onToast: (m: string) => void
  onOpenPdf: () => void
  onOpenTemplates: () => void
}) {
  const wb = useStore((s) => s.wb)
  const sel = useStore((s) => s.sel)
  const fileName = useStore((s) => s.fileName)
  const undoStack = useStore((s) => s.undoStack)
  const redoStack = useStore((s) => s.redoStack)
  const undo = useStore((s) => s.undo)
  const redo = useStore((s) => s.redo)
  const formatRange = useStore((s) => s.formatRange)
  const clearRange = useStore((s) => s.clearRange)
  const selRange = useStore((s) => s.selRange)
  const loadWorkbook = useStore((s) => s.loadWorkbook)
  const insertRows = useStore((s) => s.insertRows)
  const deleteRows = useStore((s) => s.deleteRows)
  const insertCols = useStore((s) => s.insertCols)
  const deleteCols = useStore((s) => s.deleteCols)
  const addImage = useStore((s) => s.addImage)
  const mergeRange = useStore((s) => s.mergeRange)
  const unmergeRange = useStore((s) => s.unmergeRange)

  const fileRef = useRef<HTMLInputElement>(null)
  const imageRef = useRef<HTMLInputElement>(null)
  const [borderStyle, setBorderStyle] = useState<CellBorderStyle | 'none'>('thin')
  const [borderColor, setBorderColor] = useState('#000000')
  const [borderSides, setBorderSides] = useState<'all' | 'top' | 'right' | 'bottom' | 'left'>('all')
  const sheet = wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]
  const focusStyle: CellStyle = sheet.cells[ref(sel.focus.row, sel.focus.col)]?.s ?? {}
  const r = selRange()

  const apply = (patch: CellStyle) => formatRange(sheet.id, r, patch)
  const applyBorder = () => {
    const border = borderStyle === 'none' ? undefined : { style: borderStyle, color: borderColor }
    const sides = borderSides === 'all' ? ['Top', 'Right', 'Bottom', 'Left'] : [
      borderSides[0].toUpperCase() + borderSides.slice(1),
    ]
    const patch: CellStyle = {}
    for (const side of sides) (patch as any)[`border${side}`] = border
    apply(patch)
  }

  const openFile = async (file: File) => {
    if (!isAccepted(file.name)) return onToast(`지원하지 않는 형식입니다: ${file.name}`)
    try {
      const wbNew = parseFile(file.name, await file.arrayBuffer())
      loadWorkbook(wbNew, file.name)
      onToast(`${file.name} 불러옴 · 시트 ${wbNew.sheets.length}개`)
    } catch (e: any) {
      onToast(`파일을 읽지 못했습니다: ${e?.message ?? e}`)
    }
  }

  const insertImage = async (file: File) => {
    if (!SHEET_IMAGE_TYPES.has(file.type)) return onToast('PNG, JPG, WebP, GIF 이미지만 올릴 수 있습니다.')
    if (file.size > MAX_SHEET_IMAGE_BYTES) return onToast('이미지는 한 장당 3MB까지 올릴 수 있습니다.')
    const currentBytes = wb.sheets.flatMap((s) => s.images ?? [])
      .reduce((sum, image) => sum + Math.ceil((image.src.length * 3) / 4), 0)
    if (currentBytes + file.size > MAX_WORKBOOK_IMAGE_BYTES)
      return onToast('통합 문서의 이미지 용량은 합계 15MB까지입니다.')
    try {
      const src = await fileToDataUrl(file)
      const natural = await imageSize(src)
      const scale = Math.min(1, 420 / natural.width, 320 / natural.height)
      const x = Array.from({ length: Math.max(0, sel.focus.col - 1) }, (_, i) => colWidthOf(sheet, i + 1))
        .reduce((a, b) => a + b, 0)
      const y = Array.from({ length: Math.max(0, sel.focus.row - 1) }, (_, i) => rowHeightOf(sheet, i + 1))
        .reduce((a, b) => a + b, 0)
      addImage(sheet.id, {
        id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: file.name, src, x, y,
        width: Math.max(40, Math.round(natural.width * scale)),
        height: Math.max(40, Math.round(natural.height * scale)),
      })
      onToast(`${file.name} 이미지를 시트에 추가했습니다.`)
    } catch (e: any) {
      onToast(e?.message ?? '이미지를 추가하지 못했습니다.')
    }
  }

  return (
    <div className="toolbar">
      <div className="brand">
        🧮 AI Sheet
        <small title={fileName}>{fileName}</small>
      </div>

      <input
        ref={fileRef}
        type="file"
        hidden
        accept=".xlsx,.xlsm,.xlsb,.xls,.csv,.tsv,.txt,.json,.ods"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void openFile(f)
          e.target.value = ''
        }}
      />
      <input
        ref={imageRef}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void insertImage(f)
          e.target.value = ''
        }}
      />
      <button className="tb-btn" onClick={() => fileRef.current?.click()} title="파일 열기">
        📂 열기
      </button>
      <button className="tb-btn" onClick={() => imageRef.current?.click()} title="선택한 셀 위치에 이미지 넣기">
        🖼 이미지
      </button>
      <button className="tb-btn" onClick={onOpenTemplates} title="디자인과 수식이 포함된 시트 템플릿">
        ✨ 템플릿
      </button>
      <button
        className="tb-btn"
        title="xlsx 로 저장 (수식 유지)"
        onClick={() => {
          const name = fileName.replace(/\.(csv|tsv|txt|json|ods|xls[mbx]?)$/i, '') || 'sheet'
          download(toXlsxBlob(wb), `${name}.xlsx`)
          onToast(`${name}.xlsx 저장`)
        }}
      >
        💾 xlsx
      </button>
      <button
        className="tb-btn"
        title="현재 시트를 CSV 로 저장 (계산된 값)"
        onClick={() => {
          const name = fileName.replace(/\.[^.]+$/, '') || 'sheet'
          download(new Blob(['﻿' + toCsv(wb, sheet.id)], { type: 'text/csv;charset=utf-8' }), `${name}-${sheet.name}.csv`)
          onToast('CSV 저장')
        }}
      >
        csv
      </button>
      <button className="tb-btn" title="PDF 만들기 (Ctrl+P)" onClick={onOpenPdf}>
        🖨 PDF
      </button>

      <div className="tb-sep" />
      <button className="tb-btn" disabled={!undoStack.length} onClick={undo} title="되돌리기 (Ctrl+Z)">
        ↶
      </button>
      <button className="tb-btn" disabled={!redoStack.length} onClick={redo} title="다시 실행 (Ctrl+Y)">
        ↷
      </button>

      <div className="tb-sep" />
      <select
        className="tb-select tb-font"
        value={focusStyle.fontFamily ?? ''}
        onChange={(e) => apply({ fontFamily: e.target.value })}
        title="글꼴"
      >
        <option value="">기본 글꼴</option>
        <option value="Aptos">Aptos</option>
        <option value="Calibri">Calibri</option>
        <option value="맑은 고딕">맑은 고딕</option>
        <option value="Arial">Arial</option>
        <option value="Noto Sans KR">Noto Sans KR</option>
      </select>
      <input
        className="tb-size"
        type="number"
        min={6}
        max={72}
        value={focusStyle.fontSize ?? 13}
        onChange={(e) => apply({ fontSize: Math.max(6, Math.min(72, Number(e.target.value) || 13)) })}
        title="글자 크기"
      />
      <button className="tb-btn" onClick={() => apply({ fontSize: Math.max(6, (focusStyle.fontSize ?? 13) - 1) })} title="글자 크기 줄이기">가−</button>
      <button className="tb-btn" onClick={() => apply({ fontSize: Math.min(72, (focusStyle.fontSize ?? 13) + 1) })} title="글자 크기 늘리기">가＋</button>
      <button
        className={`tb-btn${focusStyle.bold ? ' on' : ''}`}
        onClick={() => apply({ bold: !focusStyle.bold })}
        title="굵게 (Ctrl+B)"
        style={{ fontWeight: 700 }}
      >
        B
      </button>
      <button
        className={`tb-btn${focusStyle.italic ? ' on' : ''}`}
        onClick={() => apply({ italic: !focusStyle.italic })}
        title="기울임 (Ctrl+I)"
        style={{ fontStyle: 'italic' }}
      >
        I
      </button>
      <button
        className={`tb-btn${focusStyle.underline ? ' on' : ''}`}
        onClick={() => apply({ underline: !focusStyle.underline })}
        title="밑줄 (Ctrl+U)"
        style={{ textDecoration: 'underline' }}
      >
        U
      </button>

      <input
        className="tb-color"
        type="color"
        value={focusStyle.color ?? '#1f2328'}
        onChange={(e) => apply({ color: e.target.value })}
        title="글자색"
      />
      <input
        className="tb-color"
        type="color"
        value={focusStyle.bg ?? '#ffffff'}
        onChange={(e) => apply({ bg: e.target.value })}
        title="배경색"
      />

      <div className="tb-sep" />
      {(['left', 'center', 'right'] as const).map((a) => (
        <button
          key={a}
          className={`tb-btn${focusStyle.align === a ? ' on' : ''}`}
          onClick={() => apply({ align: focusStyle.align === a ? undefined : a })}
          title={`${a} 정렬`}
        >
          {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
        </button>
      ))}
      {(['top', 'middle', 'bottom'] as const).map((a) => (
        <button
          key={a}
          className={`tb-btn${focusStyle.verticalAlign === a ? ' on' : ''}`}
          onClick={() => apply({ verticalAlign: focusStyle.verticalAlign === a ? undefined : a })}
          title={`세로 ${a} 정렬`}
        >
          {a === 'top' ? '↥' : a === 'middle' ? '↕' : '↧'}
        </button>
      ))}
      <button
        className={`tb-btn${focusStyle.wrap ? ' on' : ''}`}
        onClick={() => apply({ wrap: !focusStyle.wrap })}
        title="자동 줄바꿈"
      >
        ↵
      </button>
      <button className="tb-btn" onClick={() => apply({ indent: Math.max(0, (focusStyle.indent ?? 0) - 1) })} title="내어쓰기">⇤</button>
      <button className="tb-btn" onClick={() => apply({ indent: Math.min(15, (focusStyle.indent ?? 0) + 1) })} title="들여쓰기">⇥</button>
      <select
        className="tb-select"
        value={focusStyle.textRotation ?? 0}
        onChange={(e) => apply({ textRotation: Number(e.target.value) })}
        title="텍스트 방향"
      >
        <option value="0">가로</option>
        <option value="45">↗ 45°</option>
        <option value="90">세로 90°</option>
        <option value="-45">↘ -45°</option>
      </select>
      <button
        className="tb-btn"
        onClick={() => {
          apply({ align: 'center', verticalAlign: 'middle' })
          mergeRange(sheet.id, r)
        }}
        disabled={r.r1 === r.r2 && r.c1 === r.c2}
        title="병합하고 가운데 맞춤"
      >
        병합＋가운데
      </button>
      <button className="tb-btn" onClick={() => unmergeRange(sheet.id, r)} title="셀 병합 해제">병합해제</button>

      <div className="tb-sep" />
      <select className="tb-select" value={borderSides} onChange={(e) => setBorderSides(e.target.value as any)} title="테두리 위치">
        <option value="all">전체 테두리</option>
        <option value="top">위쪽</option>
        <option value="right">오른쪽</option>
        <option value="bottom">아래쪽</option>
        <option value="left">왼쪽</option>
      </select>
      <select className="tb-select" value={borderStyle} onChange={(e) => setBorderStyle(e.target.value as any)} title="테두리 종류와 두께">
        <option value="thin">가는 실선</option>
        <option value="medium">중간 실선</option>
        <option value="thick">굵은 실선</option>
        <option value="dashed">파선</option>
        <option value="dotted">점선</option>
        <option value="double">이중선</option>
        <option value="none">없음</option>
      </select>
      <input className="tb-color" type="color" value={borderColor} onChange={(e) => setBorderColor(e.target.value)} title="테두리색" />
      <button className="tb-btn" onClick={applyBorder} title="선택한 셀에 테두리 적용">선 적용</button>

      <select
        className="tb-select"
        value={focusStyle.numFmt ?? ''}
        onChange={(e) => apply({ numFmt: e.target.value })}
        title="표시 형식"
      >
        {NUM_FORMATS.map((f) => (
          <option key={f.label} value={f.fmt}>
            {f.label}
          </option>
        ))}
      </select>
      <button className="tb-btn" onClick={() => apply({ numFmt: '₩#,##0' })} title="통화 표시 형식">₩</button>
      <button className="tb-btn" onClick={() => apply({ numFmt: '0%' })} title="백분율 표시 형식">%</button>
      <button className="tb-btn" onClick={() => apply({ numFmt: '#,##0' })} title="천 단위 구분 기호">,</button>
      <button
        className="tb-btn"
        onClick={() => {
          const current = focusStyle.numFmt ?? '#,##0'
          const dot = current.indexOf('.')
          const decimals = dot < 0 ? 0 : (current.slice(dot + 1).match(/0/g) ?? []).length
          apply({ numFmt: decimals ? current.replace(/\.(0+)/, `.${'0'.repeat(Math.max(0, decimals - 1))}`).replace(/\.$/, '') : current })
        }}
        title="소수 자릿수 줄이기"
      >.0←</button>
      <button
        className="tb-btn"
        onClick={() => {
          const current = focusStyle.numFmt || '#,##0'
          apply({ numFmt: current.includes('.') ? current.replace(/\.(0+)/, (_, z) => `.${z}0`) : `${current}.0` })
        }}
        title="소수 자릿수 늘리기"
      >.00→</button>

      <div className="tb-sep" />
      <button className="tb-btn" onClick={() => insertRows(sheet.id, r.r1, r.r2 - r.r1 + 1)} title="위에 행 삽입">
        ＋행
      </button>
      <button className="tb-btn" onClick={() => deleteRows(sheet.id, r.r1, r.r2 - r.r1 + 1)} title="선택 행 삭제">
        －행
      </button>
      <button className="tb-btn" onClick={() => insertCols(sheet.id, r.c1, r.c2 - r.c1 + 1)} title="왼쪽에 열 삽입">
        ＋열
      </button>
      <button className="tb-btn" onClick={() => deleteCols(sheet.id, r.c1, r.c2 - r.c1 + 1)} title="선택 열 삭제">
        －열
      </button>
      <button className="tb-btn" onClick={() => clearRange(sheet.id, r, 'formats')} title="서식 지우기">
        서식지움
      </button>
    </div>
  )
}
